import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import * as S from './Shops.styles';
import { Footer } from '@/components/Footer/Footer';
import { ShopsList } from '@/components/Shops/ShopsList';
import { Header } from '@/components/Header';
import { PageTopControls } from '@/components/PageTopControls';
import { Toast } from '@/components/UI/Toast';
import {
  GET_SHOPS,
  type GetShopsData,
  type GetShopsVars,
} from '@/api/queries/shops.queries';
import type { ShopOrderEnum } from '@/api/types/shop.types';
import { InfiniteLoader } from '@/components/InfiniteLoader/InfiniteLoader';
import { ShopsEmptySearchResults } from '@/components/Shops/ShopsEmptySearchResults';
import { mergeShopsResults } from '@/utils/helpers';
import { SHOPS_LIST_PAGE_SIZE } from '@/constants/common';
import { ShopsCategoryFilter } from '@/components/Shops/ShopsFilters/ShopsCategoryFilter';
import { ShopsSearch } from '@/components/Shops/ShopsFilters/ShopsSearch';
import { ShopsSortFilter } from '@/components/Shops/ShopsFilters/ShopsSortFilter';
import type { Category } from '@/api/types/categories.types';

const SEARCH_DEBOUNCE_MS = 300;

function mapSortToOrder(value: string): ShopOrderEnum {
  switch (value) {
    case 'count':
      return 'BY_ADS_COUNT';
    case 'popularity':
      return 'BY_POPULARITY';
    case 'name':
      return 'BY_NAME_ASC';
    case 'name-reverse':
      return 'BY_NAME_DESC';
    default:
      return 'DEFAULT';
  }
}

export const ShopsView: React.FC = () => {
  const { t } = useTranslation();
  const isBrowser = typeof window !== 'undefined';

  const [seed, setSeed] = useState<number>(() =>
    Math.floor(Math.random() * 1001)
  );
  const [order, setOrder] = useState<ShopOrderEnum>('DEFAULT');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isLoadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(
      () => setDebouncedSearch(search),
      SEARCH_DEBOUNCE_MS
    );
    return () => window.clearTimeout(id);
  }, [search]);

  useEffect(() => {
    order === 'DEFAULT' && setSeed(Math.floor(Math.random() * 1001));
  }, [search, categoryId, order]);

  const queryVariables = useMemo<GetShopsVars>(
    () => ({
      first: SHOPS_LIST_PAGE_SIZE,
      order,
      search: debouncedSearch.trim() || null,
      categoriesIds: categoryId ? [categoryId] : null,
      seed,
    }),
    [order, debouncedSearch, categoryId]
  );

  const { data, loading, fetchMore } = useQuery<GetShopsData, GetShopsVars>(
    GET_SHOPS,
    {
      variables: queryVariables,
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      skip: !isBrowser,
    }
  );

  const shopsConnection = data?.shops;

  const shops = shopsConnection?.nodes ?? [];
  const { endCursor, hasNextPage } = shopsConnection?.pageInfo ?? {};

  const listLoading = loading && shops.length === 0;

  const hasActiveShopFilters =
    debouncedSearch.trim() !== '' || categoryId != null || order !== 'DEFAULT';

  const showNoMatchingShops =
    hasActiveShopFilters && !listLoading && shops.length === 0;

  const handleLoadMore = useCallback(() => {
    if (
      listLoading ||
      isLoadingMore ||
      !hasNextPage ||
      !endCursor ||
      !navigator.onLine
    )
      return;

    setLoadingMore(true);
    fetchMore({
      variables: { after: endCursor, seed },
      updateQuery: (prevResult, { fetchMoreResult }) =>
        mergeShopsResults(prevResult, fetchMoreResult),
    }).finally(() => setLoadingMore(false));
  }, [listLoading, isLoadingMore, hasNextPage, endCursor, fetchMore]);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const handleCategoryChange = useCallback((category: Category | null) => {
    setCategoryId(category ? String(category.id) : null);
  }, []);

  const handleSortChange = useCallback((sortValue: string) => {
    setOrder(mapSortToOrder(String(sortValue)));
  }, []);

  return (
    <>
      <PageTopControls>
        <Header hasBurger />
      </PageTopControls>

      <S.ShopsContentWrap>
        <S.ShopsContentInner>
          <S.PageTitle $variant="1">{t('common.shops')}</S.PageTitle>
          <S.TopFiltersRow>
            <S.FiltersBox>
              <ShopsSearch
                searchValue={search}
                onSearchChange={handleSearchChange}
              />
              <S.FiltersControls>
                <ShopsCategoryFilter onCategoryChange={handleCategoryChange} />
                <ShopsSortFilter onSortChange={handleSortChange} />
              </S.FiltersControls>
            </S.FiltersBox>
          </S.TopFiltersRow>
          {showNoMatchingShops ? (
            <ShopsEmptySearchResults />
          ) : (
            <ShopsList shops={shops} loading={listLoading} />
          )}
        </S.ShopsContentInner>
      </S.ShopsContentWrap>
      {!showNoMatchingShops && (
        <InfiniteLoader onVisit={handleLoadMore} rootMargin={200} />
      )}
      <Footer />
      <Toast />
    </>
  );
};
